import React from 'react';
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Button,
  Icon,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Tooltip,
  IconButton
} from '@material-ui/core';

import { DeleteOutline } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { Title, Loader, ReloadIconButton } from '../../elements';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import { notifications, extractApiErrorMessage } from '../../utils';
import { useStyles } from './styles';
import { ApiService } from '../../hooks';
import {
  HealthSystemDomainEmissionSourcesViewModelsEmissionSourceViewModel,
  HealthSystemDALEnumsEmissionSourceRole,
  HealthSystemDomainEmissionSourcesCommandHandlersUpdateEmissionSourceCommand,
  HealthSystemDomainEmissionSourcesCommandHandlersCreateEmissionSourceCommand
} from '../../api/HealthSystemApi';
import type { TFunction } from 'i18next';

const EmissionSourceSettings: React.FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const apiService = new ApiService();
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [emissions, setEmissions] = React.useState<
    HealthSystemDomainEmissionSourcesViewModelsEmissionSourceViewModel[]
  >([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);

  const [form, setForm] = React.useState<
    Partial<HealthSystemDomainEmissionSourcesCommandHandlersUpdateEmissionSourceCommand>
  >({
    id: undefined,
    sourceRole: undefined,
    name: '',
    unitOfMeasure: '',
    co2PerUnit: 0
  });

  const loadEmissions = async () => {
    setIsBusy(true);
    const currentPage = page === 0 ? 1 : page + 1;

    apiService.api.emissionSourceSettings
      .emissionSourceSettingsGetAll({ Page: currentPage, Size: rowsPerPage })
      .then((result) => {
        setEmissions(result.data.data);
        setTotal(result.data.total);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (!loaded) loadEmissions();
  }, [loaded]);

  const handleOpenCreateDialog = () => {
    setForm({ sourceRole: undefined, name: '', unitOfMeasure: '', co2PerUnit: 0 });
    setEditMode(false);
    setDialogOpen(true);
  };

  const handleOpenEditDialog = (row: HealthSystemDomainEmissionSourcesCommandHandlersUpdateEmissionSourceCommand) => {
    setForm({
      id: row.id,
      sourceRole: row.sourceRole,
      name: row.name,
      unitOfMeasure: row.unitOfMeasure,
      co2PerUnit: row.co2PerUnit
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleSubmitEmission = () => {
    setIsBusy(true);

    // `form` is filled field by field, so it is a Partial; only `id` is checked here, hence the casts.
    const promise = form.id
      ? apiService.api.emissionSourceSettings.emissionSourceSettingsUpdateEmissionSource(
          form as HealthSystemDomainEmissionSourcesCommandHandlersUpdateEmissionSourceCommand
        )
      : apiService.api.emissionSourceSettings.emissionSourceSettingsCreateEmissionSource(
          form as HealthSystemDomainEmissionSourcesCommandHandlersCreateEmissionSourceCommand
        );

    promise
      .then(() => {
        notifications.success(t(editMode ? 'f-emission-update-success' : 'f-emission-create-success'));
        setDialogOpen(false);
        setLoaded(false);
      })
      .catch((e) => {
        notifications.error(
          t(editMode ? 'f-emission-update-error' : 'f-emission-create-error', {
            error: e.toString()
          })
        );
      })
      .finally(() => setIsBusy(false));
  };

  const handleDeleteEmission = (id: string) => {
    if (!window.confirm(t('f-emission-delete-confirm'))) return;
    setIsBusy(true);

    apiService.api.emissionSourceSettings
      .emissionSourceSettingsDelete({ id })
      .then(() => {
        notifications.success(t('f-emission-delete-success'));
        setLoaded(false);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => setIsBusy(false));
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };

  const getEmissionSourceRoleLabel = (role: HealthSystemDALEnumsEmissionSourceRole, t: TFunction) => {
    switch (role) {
      case 1:
        return `${t('f-emission-role-input')}`;
      case 2:
        return `${t('f-emission-role-output')}`;
      default:
        return `${t('f-unknown')} (${role})`;
    }
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Grid container className={classes.paperHeader}>
            <Grid item container md={6} xs={6} spacing={1}>
              <Grid item>
                <Icon color="primary">science</Icon>
              </Grid>
              <Grid item>
                <Title>{t('f-emission-title')}</Title>
              </Grid>
              <Grid item>
                <Chip label={total} variant="outlined" />
              </Grid>
            </Grid>
            <Grid item container md={6} xs={6} className={classes.justifyRight}>
              <Grid item>
                <ReloadIconButton action={() => setLoaded(false)} />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenCreateDialog}
                  startIcon={<Icon>add_circle_outline</Icon>}
                >
                  {t('f-com-create-new')}
                </Button>
              </Grid>
            </Grid>
          </Grid>

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {loaded && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead className={classes.tableHeader}>
                  <TableRow>
                    <TableCell align="center">{t('f-emission-name')}</TableCell>
                    <TableCell align="center">{t('f-emission-role')}</TableCell>
                    <TableCell align="center">{t('f-emission-unit')}</TableCell>
                    <TableCell align="center">{t('f-emission-co2')}</TableCell>
                    <TableCell align="center">{t('m-global-actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {emissions.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell align="center">{row.name}</TableCell>
                      <TableCell align="center">{getEmissionSourceRoleLabel(row.sourceRole, t)}</TableCell>
                      <TableCell align="center">{row.unitOfMeasure}</TableCell>
                      <TableCell align="center">{row.co2PerUnit}</TableCell>
                      <TableCell align="center">
                        <Tooltip arrow title={t('f-com-edit')}>
                          <IconButton
                            id={`upd-${row.id}`}
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEditDialog(row)}
                          >
                            <Icon>{'edit'}</Icon>
                          </IconButton>
                        </Tooltip>
                        <IconButton onClick={() => handleDeleteEmission(row.id)}>
                          <DeleteOutline color="error" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                      count={total}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )}
        </Grid>
      </Grid>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{editMode ? t('f-emission-edit') : t('f-emission-create')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('f-emission-name')}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            margin="dense"
          />
          <TextField
            select
            fullWidth
            label={t('f-emission-role')}
            value={form.sourceRole ?? ''}
            onChange={(e) =>
              setForm({
                ...form,
                // The role is a 1 | 2 union and parseInt returns a number; the menu offers only those two.
                sourceRole: parseInt(e.target.value, 10) as HealthSystemDALEnumsEmissionSourceRole
              })
            }
            margin="dense"
          >
            <MenuItem value={1}>{t('f-emission-role-input')}</MenuItem>
            <MenuItem value={2}>{t('f-emission-role-output')}</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label={t('f-emission-unit')}
            value={form.unitOfMeasure}
            onChange={(e) => setForm({ ...form, unitOfMeasure: e.target.value })}
            margin="dense"
          />
          <TextField
            fullWidth
            label={t('f-emission-co2')}
            type="number"
            value={form.co2PerUnit}
            inputProps={{ min: 0 }}
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              if (!isNaN(value) && value >= 0) {
                setForm({ ...form, co2PerUnit: value });
              }
            }}
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('f-com-cancel')}</Button>
          <Button onClick={handleSubmitEmission} color="primary" variant="contained">
            {t('f-com-save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmissionSourceSettings;
